import React from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";
import { SubpageNav, SubpageFooter } from "@/components/SubpageLayout";
import SubpageHero from "@/components/SubpageHero";
import {
  Shield, Lock, Database, FileCheck, Check, Heart, Eye, KeyRound,
  AlertTriangle, Globe, Zap, Users, FlaskConical,
} from "lucide-react";

const TX = {
  bg: {
    seo: "Сигурност и поверителност",
    hero_title: "Вашите здравни данни са защитени",
    hero_sub: "Научете как Nutramilo пази вашата информация и ви дава контрол над нея.",
    // Encryption — simplified for patients
    enc_title: "Как защитаваме вашите данни",
    enc_items: [
      { icon: "Shield", title: "Защита на връзката", desc: "Цялата комуникация между вас и платформата е криптирана (HTTPS/TLS). Добавени са допълнителни HTTP headers за защита." },
      { icon: "KeyRound", title: "Защитен вход", desc: "Паролата ви е хеширана с bcrypt и не се съхранява в четим вид. При 5 неуспешни опита за вход, акаунтът се заключва за 15 минути." },
      { icon: "Eye", title: "Проследяване на достъпа", desc: "Всяко влизане и всяко критично действие се записва в защитен дневник (audit log), включително IP адрес и време." },
      { icon: "Users", title: "Контрол на достъпа", desc: "Само вие и вашият лекар (ако е назначен) имате достъп до здравните ви данни. Лекарите виждат само данните на насочените им пациенти." },
      { icon: "Database", title: "Облачна база данни", desc: "Данните се съхраняват в защитена MongoDB база. Достъпът е ограничен само до приложението чрез VPC и мрежови правила." },
      { icon: "Zap", title: "Защита от претоварване", desc: "Системата ограничава броя заявки (rate limiting), за да предотврати злоупотреба — без да пречи на нормалното ви използване." },
    ],
    // What's protected
    enc_what_title: "Какви данни съхраняваме",
    enc_encrypted: ["Лабораторни стойности (глюкоза, инсулин, холестерол и др.)", "Естрогенови метаболити", "Текущи здравни състояния", "Медикаменти", "Фамилна история", "Алергии", "AI лабораторни анализи (до 10 PDF)"],
    enc_not_encrypted: ["Имейл (за вход в акаунта)", "Име (за визуализация)", "Роля и абонамент"],
    enc_encrypted_label: "Здравни данни (защитени)",
    enc_not_label: "Системни данни (за функционалност)",
    // Data sources
    data_title: "Откъде идват данните",
    data_sub: "Nutramilo използва само проверени, научно обосновани източници.",
    data_items: [
      { name: "PubMed (NCBI)", desc: "30M+ медицински публикации — използван от Evidence Engine за проверка на AI изводи", url: "https://pubmed.ncbi.nlm.nih.gov" },
      { name: "Semantic Scholar", desc: "200M+ научни статии с Graph API — Evidence Engine консенсус и цитати", url: "https://www.semanticscholar.org" },
      { name: "USDA FoodData Central", desc: "Официална база данни на Министерство на земеделието на САЩ с 23,000+ храни", url: "https://fdc.nal.usda.gov" },
      { name: "Open Food Facts", desc: "Отворена база данни с хранителна информация за хиляди продукти", url: "https://world.openfoodfacts.org" },
      { name: "Edamam", desc: "Рецепти и подробен хранителен анализ", url: "https://www.edamam.com" },
      { name: "DrugBank / PubChem", desc: "Лекарствено-хранителни взаимодействия — какво да избягвате", url: "https://www.drugbank.com" },
      { name: "ClinVar / Open Targets", desc: "Генетични варианти и асоциации за нутригеномика", url: "https://www.ncbi.nlm.nih.gov/clinvar" },
      { name: "OpenAI GPT-5.2", desc: "4 специализирани AI агента за персонализирани анализи", url: "https://openai.com" },
    ],
    // Scientific indices section
    indices_title: "11 валидирани научни индекса",
    indices_sub: "Nutramilo е единствената платформа в Европа, която обединява всички тези рецензирани научни показатели в едно табло.",
    indices_per_food_title: "Индекси на ниво храна (Smart Food)",
    indices_per_food: [
      { name: "Nutri-Score (A-E)", desc: "Официалният EU FOP етикет — Франция, Белгия, Германия, Испания, Нидерландия, Швейцария", ref: "Santé publique France 2016 · EU Council 2020/C 286/04" },
      { name: "NRF11.3", desc: "Nutrient Rich Foods index — хранителна плътност на калория (11 положителни − 3 ограничителни)", ref: "Drewnowski 2014, Public Health Nutr 17(12):2763" },
      { name: "NOVA класификация", desc: "Ултра-преработени храни (UPF) — 1 (непреработена) до 4 (ултра-преработена). WHO/PAHO endorsed", ref: "Monteiro 2018, Public Health Nutr 21(1):5" },
      { name: "Glycemic Load (GL)", desc: "Гликемично натоварване = GI × налични въглехидрати / 100. Ниско <10, високо >20", ref: "Foster-Powell 2002, Am J Clin Nutr 76(1):5" },
      { name: "Omega-6:3 ratio", desc: "Противовъзпалителен баланс. Оптимално ≤4:1, съвременна западна диета 15-20:1", ref: "Simopoulos 2016, Nutrients 8(3):128" },
      { name: "PRAL", desc: "Potential Renal Acid Load — алкален/кисел потенциал. Важен за бъбреци, камъни, остеопороза", ref: "Remer & Manz 1995, J Am Diet Assoc 95(7):791" },
      { name: "Инсулинов товар", desc: "Диетично инсулиново натоварване — персонализиран дневен бюджет. Собствен модел на Nutramilo", ref: "Bao 2011 · Bell 2015 · собствена калибрация" },
      { name: "FODMAP", desc: "Ферментиращи олиго-, ди-, моно-захариди и полиоли. Важен за IBS/SIBO", ref: "Monash University FODMAP DB" },
      { name: "Satiety Score", desc: "Наситеност на база протеин, фибри, енергийна плътност и палатабилност", ref: "Holt 1995 · собствена калибрация" },
    ],
    indices_patterns_title: "Хранителни модели (Dashboard tile)",
    indices_patterns: [
      { name: "Mediterranean (MEDI-LITE)", desc: "Средиземноморска диета. PREDIMED trial: 30% по-малък риск от ССЗ при диабет", ref: "Sofi 2014, Public Health Nutr 17(12):2769 · Estruch 2013 NEJM" },
      { name: "DASH", desc: "Dietary Approaches to Stop Hypertension. Понижава кръвно налягане значително", ref: "Fung 2008, Arch Intern Med 168(7):713 · Appel 1997 NEJM" },
      { name: "hPDI", desc: "healthful Plant-based Diet Index. 20% по-малък риск от диабет тип 2", ref: "Satija 2017, J Am Coll Cardiol 70(4):411" },
      { name: "MIND", desc: "Mediterranean-DASH Intervention for Neurodegenerative Delay. 53% по-малък риск от Алцхаймер", ref: "Morris 2015, Alzheimers Dement 11(9):1007" },
    ],
    // GDPR
    gdpr_title: "Вашите права",
    gdpr_sub: "Nutramilo спазва Европейския регламент за защита на личните данни (GDPR).",
    gdpr_items: [
      "Данните ви се обработват само за предоставяне на услугата",
      "Можете да поискате изтриване на акаунта и всички данни по всяко време",
      "Чувствителните здравни данни са защитени чрез контрол на достъпа",
      "Всеки достъп до данните ви се записва в audit log",
      "Събираме само минимално необходимата информация",
      "Данните ви никога не се продават на трети страни",
      "Имате право да изтеглите всички ваши данни (Data Portability)",
    ],
    disclaimer: "Nutramilo не е медицинско изделие и не замества лекарска консултация.",
  },
  en: {
    seo: "Security & Privacy",
    hero_title: "Your health data is protected",
    hero_sub: "Learn how Nutramilo protects your information and gives you control over it.",
    enc_title: "How we protect your data",
    enc_items: [
      { icon: "Shield", title: "Connection protection", desc: "All communication between you and the platform is encrypted (HTTPS/TLS). Additional HTTP security headers are applied." },
      { icon: "KeyRound", title: "Secure login", desc: "Your password is hashed with bcrypt and never stored in readable form. After 5 failed attempts, the account locks for 15 minutes." },
      { icon: "Eye", title: "Access tracking", desc: "Every login and every critical action is recorded in a secure audit log, including IP address and time." },
      { icon: "Users", title: "Access control", desc: "Only you and your assigned doctor have access to your health data. Doctors can only see their assigned patients' data." },
      { icon: "Database", title: "Cloud database", desc: "Data is stored in a secured MongoDB database. Access is restricted to the application only via VPC and network rules." },
      { icon: "Zap", title: "Rate limiting", desc: "The system limits request rates to prevent abuse — without interfering with your normal usage." },
    ],
    enc_what_title: "What data we store",
    enc_encrypted: ["Lab values (glucose, insulin, cholesterol, etc.)", "Estrogen metabolites", "Current health conditions", "Medications", "Family history", "Allergies", "AI lab analyses (up to 10 PDFs)"],
    enc_not_encrypted: ["Email (for login)", "Name (for display)", "Role and subscription"],
    enc_encrypted_label: "Health data (protected)",
    enc_not_label: "System data (for functionality)",
    data_title: "Where the data comes from",
    data_sub: "Nutramilo uses only verified, scientifically backed sources.",
    data_items: [
      { name: "PubMed (NCBI)", desc: "30M+ medical publications — used by Evidence Engine to verify AI claims", url: "https://pubmed.ncbi.nlm.nih.gov" },
      { name: "Semantic Scholar", desc: "200M+ scientific articles via Graph API — Evidence Engine consensus + citations", url: "https://www.semanticscholar.org" },
      { name: "USDA FoodData Central", desc: "Official USDA database with 23,000+ foods", url: "https://fdc.nal.usda.gov" },
      { name: "Open Food Facts", desc: "Open database with nutritional information for thousands of products", url: "https://world.openfoodfacts.org" },
      { name: "Edamam", desc: "Recipes and detailed nutritional analysis", url: "https://www.edamam.com" },
      { name: "DrugBank / PubChem", desc: "Drug-food interactions — what to avoid", url: "https://www.drugbank.com" },
      { name: "ClinVar / Open Targets", desc: "Genetic variants and associations for nutrigenomics", url: "https://www.ncbi.nlm.nih.gov/clinvar" },
      { name: "OpenAI GPT-5.2", desc: "4 specialized AI agents for personalized analyses", url: "https://openai.com" },
    ],
    // Scientific indices
    indices_title: "11 Validated Scientific Indices",
    indices_sub: "Nutramilo is the only platform in Europe that combines all these peer-reviewed indices in one dashboard.",
    indices_per_food_title: "Per-food indices (Smart Food)",
    indices_per_food: [
      { name: "Nutri-Score (A-E)", desc: "Official EU FOP label — France, Belgium, Germany, Spain, Netherlands, Switzerland", ref: "Santé publique France 2016 · EU Council 2020/C 286/04" },
      { name: "NRF11.3", desc: "Nutrient Rich Foods index — nutrient density per calorie (11 positive − 3 limit)", ref: "Drewnowski 2014, Public Health Nutr 17(12):2763" },
      { name: "NOVA classification", desc: "Ultra-processed foods (UPF) — 1 (unprocessed) to 4 (ultra-processed). WHO/PAHO endorsed", ref: "Monteiro 2018, Public Health Nutr 21(1):5" },
      { name: "Glycemic Load (GL)", desc: "GL = GI × available carbs / 100. Low <10, high >20", ref: "Foster-Powell 2002, Am J Clin Nutr 76(1):5" },
      { name: "Omega-6:3 ratio", desc: "Anti-inflammatory balance. Optimal ≤4:1, modern Western diet 15-20:1", ref: "Simopoulos 2016, Nutrients 8(3):128" },
      { name: "PRAL", desc: "Potential Renal Acid Load — alkaline/acid potential. Important for kidney, stones, osteoporosis", ref: "Remer & Manz 1995, J Am Diet Assoc 95(7):791" },
      { name: "DIL", desc: "Dietary Insulin Load — personalized daily budget. Proprietary Nutramilo model", ref: "Bao 2011 · Bell 2015 · proprietary calibration" },
      { name: "FODMAP", desc: "Fermentable Oligo-, Di-, Mono-saccharides And Polyols. Important for IBS/SIBO", ref: "Monash University FODMAP DB" },
      { name: "Satiety Score", desc: "Fullness based on protein, fiber, energy density and palatability", ref: "Holt 1995 · proprietary calibration" },
    ],
    indices_patterns_title: "Dietary patterns (Dashboard tile)",
    indices_patterns: [
      { name: "Mediterranean (MEDI-LITE)", desc: "Mediterranean diet. PREDIMED trial: 30% lower CVD risk in diabetes", ref: "Sofi 2014, Public Health Nutr 17(12):2769 · Estruch 2013 NEJM" },
      { name: "DASH", desc: "Dietary Approaches to Stop Hypertension. Significantly lowers blood pressure", ref: "Fung 2008, Arch Intern Med 168(7):713 · Appel 1997 NEJM" },
      { name: "hPDI", desc: "Healthful Plant-based Diet Index. 20% lower T2D risk", ref: "Satija 2017, J Am Coll Cardiol 70(4):411" },
      { name: "MIND", desc: "Mediterranean-DASH Intervention for Neurodegenerative Delay. 53% lower Alzheimer risk", ref: "Morris 2015, Alzheimers Dement 11(9):1007" },
    ],
    // GDPR
    gdpr_title: "Your rights",
    gdpr_sub: "Nutramilo complies with the European General Data Protection Regulation (GDPR).",
    gdpr_items: [
      "Your data is processed only for service delivery",
      "You can request account and data deletion at any time",
      "Sensitive data is protected via access control",
      "Every access to your data is recorded in audit log",
      "We collect only the minimum necessary information",
      "Your data is never sold to third parties",
      "You have the right to download all your data (Data Portability)",
    ],
    disclaimer: "Nutramilo is not a medical device and does not replace medical consultation.",
  },
  es: {
    seo: "Seguridad y Privacidad",
    hero_title: "Tus datos de salud están protegidos",
    hero_sub: "Descubre cómo Nutramilo protege tu información y te da control sobre ella.",
    enc_title: "Cómo protegemos tus datos",
    enc_items: [
      { icon: "Shield", title: "Protección de la conexión", desc: "Toda la comunicación entre tú y la plataforma está cifrada (HTTPS/TLS). Se aplican cabeceras de seguridad HTTP adicionales." },
      { icon: "KeyRound", title: "Inicio de sesión seguro", desc: "Tu contraseña se almacena con hash bcrypt y nunca se guarda en forma legible. Tras 5 intentos fallidos, la cuenta se bloquea durante 15 minutos." },
      { icon: "Eye", title: "Seguimiento de accesos", desc: "Cada inicio de sesión y cada acción crítica quedan registrados en un registro de auditoría seguro, incluyendo dirección IP y hora." },
      { icon: "Users", title: "Control de acceso", desc: "Solo tú y tu médico asignado tienen acceso a tus datos de salud. Los médicos solo pueden ver los datos de sus pacientes asignados." },
      { icon: "Database", title: "Base de datos en la nube", desc: "Los datos se almacenan en una base de datos MongoDB segura. El acceso está restringido únicamente a la aplicación mediante VPC y reglas de red." },
      { icon: "Zap", title: "Limitación de velocidad", desc: "El sistema limita la tasa de solicitudes para prevenir abusos, sin interferir con tu uso normal." },
    ],
    enc_what_title: "Qué datos almacenamos",
    enc_encrypted: ["Valores de laboratorio (glucosa, insulina, colesterol, etc.)", "Metabolitos de estrógeno", "Condiciones de salud actuales", "Medicamentos", "Historia familiar", "Alergias", "Análisis de laboratorio por AI (hasta 10 PDFs)"],
    enc_not_encrypted: ["Correo electrónico (para inicio de sesión)", "Nombre (para visualización)", "Rol y suscripción"],
    enc_encrypted_label: "Datos de salud (protegidos)",
    enc_not_label: "Datos del sistema (para funcionalidad)",
    data_title: "De dónde provienen los datos",
    data_sub: "Nutramilo utiliza únicamente fuentes verificadas y respaldadas científicamente.",
    data_items: [
      { name: "PubMed (NCBI)", desc: "Más de 30 millones de publicaciones médicas — utilizadas por Evidence Engine para verificar afirmaciones de AI", url: "https://pubmed.ncbi.nlm.nih.gov" },
      { name: "Semantic Scholar", desc: "Más de 200 millones de artículos científicos vía Graph API — consenso y citas de Evidence Engine", url: "https://www.semanticscholar.org" },
      { name: "USDA FoodData Central", desc: "Base de datos oficial de la USDA con más de 23.000 alimentos", url: "https://fdc.nal.usda.gov" },
      { name: "Open Food Facts", desc: "Base de datos abierta con información nutricional de miles de productos", url: "https://world.openfoodfacts.org" },
      { name: "Edamam", desc: "Recetas y análisis nutricional detallado", url: "https://www.edamam.com" },
      { name: "DrugBank / PubChem", desc: "Interacciones medicamento-alimento — qué evitar", url: "https://www.drugbank.com" },
      { name: "ClinVar / Open Targets", desc: "Variantes genéticas y asociaciones para nutrigenómica", url: "https://www.ncbi.nlm.nih.gov/clinvar" },
      { name: "OpenAI GPT-5.2", desc: "4 agentes de AI especializados para análisis personalizados", url: "https://openai.com" },
    ],
    // Scientific indices
    indices_title: "11 Índices Científicos Validados",
    indices_sub: "Nutramilo es la única plataforma en Europa que combina todos estos índices revisados por pares en un solo panel.",
    indices_per_food_title: "Índices por alimento (Smart Food)",
    indices_per_food: [
      { name: "Nutri-Score (A-E)", desc: "Etiqueta FOP oficial de la UE — Francia, Bélgica, Alemania, España, Países Bajos, Suiza", ref: "Santé publique France 2016 · EU Council 2020/C 286/04" },
      { name: "NRF11.3", desc: "Índice de Alimentos Ricos en Nutrientes — densidad de nutrientes por caloría (11 positivos − 3 limitantes)", ref: "Drewnowski 2014, Public Health Nutr 17(12):2763" },
      { name: "Clasificación NOVA", desc: "Alimentos ultraprocesados (UPF) — 1 (no procesado) a 4 (ultraprocesado). Avalado por la OMS/OPS", ref: "Monteiro 2018, Public Health Nutr 21(1):5" },
      { name: "Carga Glucémica (GL)", desc: "GL = IG × carbohidratos disponibles / 100. Baja <10, alta >20", ref: "Foster-Powell 2002, Am J Clin Nutr 76(1):5" },
      { name: "Ratio Omega-6:3", desc: "Equilibrio antiinflamatorio. Óptimo ≤4:1, dieta occidental moderna 15-20:1", ref: "Simopoulos 2016, Nutrients 8(3):128" },
      { name: "PRAL", desc: "Carga Ácida Renal Potencial — potencial alcalino/ácido. Importante para riñones, cálculos y osteoporosis", ref: "Remer & Manz 1995, J Am Diet Assoc 95(7):791" },
      { name: "DIL", desc: "Carga de Insulina Dietética — presupuesto diario personalizado. Modelo propio de Nutramilo", ref: "Bao 2011 · Bell 2015 · calibración propia" },
      { name: "FODMAP", desc: "Oligosacáridos, Disacáridos, Monosacáridos y Polioles Fermentables. Importante para SII/SIBO", ref: "Monash University FODMAP DB" },
      { name: "Índice de Saciedad", desc: "Saciedad basada en proteínas, fibra, densidad energética y palatabilidad", ref: "Holt 1995 · calibración propia" },
    ],
    indices_patterns_title: "Patrones dietéticos (panel del Dashboard)",
    indices_patterns: [
      { name: "Mediterráneo (MEDI-LITE)", desc: "Dieta mediterránea. Ensayo PREDIMED: 30% menos riesgo cardiovascular en diabetes", ref: "Sofi 2014, Public Health Nutr 17(12):2769 · Estruch 2013 NEJM" },
      { name: "DASH", desc: "Enfoques Dietéticos para Detener la Hipertensión. Reduce significativamente la presión arterial", ref: "Fung 2008, Arch Intern Med 168(7):713 · Appel 1997 NEJM" },
      { name: "hPDI", desc: "Índice de Dieta Basada en Plantas Saludable. 20% menos riesgo de diabetes tipo 2", ref: "Satija 2017, J Am Coll Cardiol 70(4):411" },
      { name: "MIND", desc: "Intervención Mediterránea-DASH para Retraso Neurodegenerativo. 53% menos riesgo de Alzheimer", ref: "Morris 2015, Alzheimers Dement 11(9):1007" },
    ],
    // GDPR
    gdpr_title: "Tus derechos",
    gdpr_sub: "Nutramilo cumple con el Reglamento General de Protección de Datos europeo (GDPR).",
    gdpr_items: [
      "Tus datos se procesan únicamente para la prestación del servicio",
      "Puedes solicitar la eliminación de tu cuenta y datos en cualquier momento",
      "Los datos sensibles están protegidos mediante control de acceso",
      "Cada acceso a tus datos queda registrado en el registro de auditoría",
      "Recopilamos únicamente la información mínima necesaria",
      "Tus datos nunca se venden a terceros",
      "Tienes derecho a descargar todos tus datos (Portabilidad de Datos)",
    ],
    disclaimer: "Nutramilo no es un dispositivo médico y no sustituye la consulta médica.",
  },
};

const ICON_MAP = { Lock, KeyRound, Eye, Users, Shield, Zap, Database };

export default function TechnicalPage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const t = TX[language] || TX.en;

  const sectionBg = isDark ? "bg-[hsl(223,47%,6%)]" : "bg-[#F9F9F7]";
  const altBg = isDark ? "bg-[hsl(223,47%,8%)]" : "bg-white";
  const heading = `text-xl sm:text-2xl font-bold tracking-tight ${isDark ? "text-white" : "text-[#1A1A2E]"}`;
  const cardCls = `p-5 rounded-2xl border ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,16%)]" : "bg-white border-slate-100 shadow-sm"}`;
  const body = `text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`;
  const badge = `inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-medium ${isDark ? "border-[hsl(223,47%,20%)] text-slate-400" : "border-slate-200 text-slate-500"}`;

  return (
    <div className={`min-h-screen ${sectionBg}`} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="technical-page">
      <SEO title={t.seo} description={t.hero_sub} path="/technical" />
      <SubpageNav />

      {/* Hero */}
      <section className={altBg}>
        <div className="max-w-3xl mx-auto px-4">
          <SubpageHero
            eyebrow={language === "bg" ? "Техническа спецификация" : language === "es" ? "Especificación técnica" : "Technical specification"}
            title={t.hero_title}
            subtitle={t.hero_sub}
            meta={language === "bg"
              ? ["Криптиране AES-256", "Сървъри в Европейския съюз", "Прозрачна методика"]
              : language === "es"
              ? ["Cifrado AES-256", "Servidores en la Unión Europea", "Metodología transparente"]
              : ["AES-256 encryption", "Servers in the European Union", "Transparent methodology"]}
            icon={Shield}
            accent="#10B981"
            testId="technical-hero"
          />
        </div>
      </section>

      {/* How We Protect */}
      <section className={`py-14 ${sectionBg}`} data-testid="tech-security">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className={badge}><Lock className="w-3 h-3" /> AES-256</span>
            <h2 className={`${heading} mt-3`}>{t.enc_title}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {t.enc_items.map((item) => {
              const Icon = ICON_MAP[item.icon] || Shield;
              return (
                <div key={item.title} className={cardCls}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: "#10B98115" }}>
                    <Icon className="w-5 h-5 text-[#10B981]" />
                  </div>
                  <h3 className={`text-sm font-bold mb-1.5 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{item.title}</h3>
                  <p className={`text-xs leading-relaxed ${isDark ? "text-slate-400" : "text-slate-500"}`}>{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What's Encrypted */}
      <section className={`py-14 ${altBg}`} data-testid="tech-encryption">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className={`${heading} text-center mb-8`}>{t.enc_what_title}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className={cardCls}>
              <div className="flex items-center gap-2 mb-3">
                <Lock className="w-4 h-4 text-[#10B981]" />
                <h3 className="text-sm font-bold text-[#10B981]">{t.enc_encrypted_label}</h3>
              </div>
              <ul className="space-y-2">{t.enc_encrypted.map(item => (
                <li key={item} className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#10B981]" />
                  <span className={`text-xs ${isDark ? "text-slate-300" : "text-slate-600"}`}>{item}</span>
                </li>
              ))}</ul>
            </div>
            <div className={cardCls}>
              <div className="flex items-center gap-2 mb-3">
                <Globe className="w-4 h-4 text-slate-400" />
                <h3 className={`text-sm font-bold ${isDark ? "text-slate-400" : "text-slate-500"}`}>{t.enc_not_label}</h3>
              </div>
              <ul className="space-y-2">{t.enc_not_encrypted.map(item => (
                <li key={item} className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" />
                  <span className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>{item}</span>
                </li>
              ))}</ul>
            </div>
          </div>
        </div>
      </section>

      {/* Data Sources */}
      <section className={`py-14 ${sectionBg}`} data-testid="tech-data-sources">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <span className={badge}><Database className="w-3 h-3" /> Sources</span>
            <h2 className={`${heading} mt-3`}>{t.data_title}</h2>
            <p className={`${body} mt-2`}>{t.data_sub}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {t.data_items.map(item => (
              <a key={item.name} href={item.url} target="_blank" rel="noopener noreferrer"
                className={`${cardCls} hover:shadow-md transition-shadow block`}>
                <h3 className={`text-sm font-bold mb-1 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{item.name}</h3>
                <p className={`text-xs ${isDark ? "text-slate-500" : "text-slate-400"}`}>{item.desc}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Scientific Indices */}
      <section className={`py-14 ${sectionBg}`} data-testid="tech-indices">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-8">
            <span className={badge}><FlaskConical className="w-3 h-3" /> Science</span>
            <h2 className={`${heading} mt-3`}>{t.indices_title}</h2>
            <p className={`${body} mt-2`}>{t.indices_sub}</p>
          </div>

          <h3 className={`text-sm font-bold mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            {t.indices_per_food_title}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
            {t.indices_per_food.map(item => (
              <div key={item.name} className={cardCls} data-testid={`index-${item.name.split(' ')[0].toLowerCase()}`}>
                <h4 className={`text-sm font-bold mb-1 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{item.name}</h4>
                <p className={`text-xs mb-2 ${isDark ? "text-slate-400" : "text-slate-500"}`}>{item.desc}</p>
                <p className={`text-[10px] italic ${isDark ? "text-slate-500" : "text-slate-400"}`}>{item.ref}</p>
              </div>
            ))}
          </div>

          <h3 className={`text-sm font-bold mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            {t.indices_patterns_title}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {t.indices_patterns.map(item => (
              <div key={item.name} className={cardCls} data-testid={`pattern-${item.name.split(' ')[0].toLowerCase()}`}>
                <h4 className={`text-sm font-bold mb-1 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{item.name}</h4>
                <p className={`text-xs mb-2 ${isDark ? "text-slate-400" : "text-slate-500"}`}>{item.desc}</p>
                <p className={`text-[10px] italic ${isDark ? "text-slate-500" : "text-slate-400"}`}>{item.ref}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GDPR */}
      <section className={`py-14 ${altBg}`} data-testid="tech-gdpr">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-8">
            <span className={badge}><FileCheck className="w-3 h-3" /> GDPR</span>
            <h2 className={`${heading} mt-3`}>{t.gdpr_title}</h2>
            <p className={`${body} mt-2`}>{t.gdpr_sub}</p>
          </div>
          <div className={cardCls}>
            <ul className="space-y-2.5">{t.gdpr_items.map(item => (
              <li key={item} className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#10B981]" />
                <span className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>{item}</span>
              </li>
            ))}</ul>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className={`py-8 ${sectionBg}`}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className={`text-xs flex items-center justify-center gap-1.5 ${isDark ? "text-slate-600" : "text-slate-400"}`}>
            <AlertTriangle className="w-3 h-3" /> {t.disclaimer}
          </p>
        </div>
      </section>

      <SubpageFooter />
    </div>
  );
}
